<template>
  <Header />
  <SideBar />
  <main id="main-content" class="ml-64 p-8">
    <div class="mx-auto">
      <div class="flex justify-between items-center mb-8">
        <div>
          <h1 class="text-2xl font-bold text-green-800">Hello, {{ authStore.user.firstName || 'User' }}</h1>
          <p class="mt-1 text-gray-600">Welcome to your console dashboard</p>
        </div>
      </div>

      <div id="stats-grid" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div class="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-500">Active API Keys</p>
              <p class="text-3xl font-bold text-gray-900">{{ apiKeysStore.activekeysCount ?? 0 }}</p>
              <p class="text-sm text-green-600 mt-1">
                <i class="fa-solid fa-key mr-1"></i>
                API keys currently active
              </p>
            </div>
            <div class="bg-blue-100 p-3 rounded-full">
              <i class="fa-solid fa-key text-blue-600 text-xl"></i>
            </div>
          </div>
        </div>

        <div class="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-500">Monthly API Usage</p>
              <p class="text-3xl font-bold text-gray-900">{{ apiKeysStore.monthlyUsageCount ?? 0 }}</p>
              <p class="text-sm text-green-600 mt-1">
                <i class="fa-solid fa-chart-line mr-1"></i>
                Requests this month
              </p>
            </div>
            <div class="bg-green-100 p-3 rounded-full">
              <i class="fa-solid fa-chart-line text-green-600 text-xl"></i>
            </div>
          </div>
        </div>

        <div class="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-500">Phone Validations</p>
              <p class="text-3xl font-bold text-gray-900">{{ requestStore.phoneNumberResults?.length ?? 0 }}</p>
              <p class="text-sm text-green-600 mt-1">
                <i class="fa-solid fa-phone mr-1"></i>
                Phone numbers verified
              </p>
            </div>
            <div class="bg-purple-100 p-3 rounded-full">
              <i class="fa-solid fa-phone text-purple-600 text-xl"></i>
            </div>
          </div>
        </div>

        <div class="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-500">Active Users</p>
              <p class="text-3xl font-bold text-gray-900">{{ userStore.users?.length ?? 0 }}</p>
              <p class="text-sm text-green-600 mt-1">
                <i class="fa-solid fa-users mr-1"></i>
                Registered users
              </p>
            </div>
            <div class="bg-orange-100 p-3 rounded-full">
              <i class="fa-solid fa-users text-orange-600 text-xl"></i>
            </div>
          </div>
        </div>
      </div>

      <div id="recent-activity" class="bg-white rounded-lg shadow-sm border border-gray-200 mb-8">
        <div class="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 class="text-lg font-semibold text-gray-800">Recent API Requests</h3>
          <a href="/apirequests" class="text-green-600 text-sm font-medium hover:text-green-700">View All</a>
        </div>

        <div v-if="apiKeysStore.loading" class="flex justify-center items-center h-48">
          <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600"></div>
        </div>

        <div v-else-if="!apiKeysStore.apiRequests?.length" class="p-8 text-center text-gray-400 text-sm">
          No recent requests
        </div>

        <div v-else class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Endpoint</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Organization</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">HTTP Status</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="req in apiKeysStore.apiRequests.slice(0, 5)" :key="req.id" class="hover:bg-gray-50 transition-colors">
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm text-gray-900">{{ formatDate(req.createdAt) }}</div>
                  <div class="text-xs text-gray-400">{{ formatTime(req.createdAt) }}</div>
                </td>
                <td class="px-6 py-4 text-sm font-mono text-gray-700 max-w-xs">
                  <span v-if="req.endpoint" :title="req.endpoint" class="block truncate max-w-xs cursor-default">
                    {{ req.endpoint }}
                  </span>
                  <span v-else class="text-gray-400 italic">No endpoint</span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  {{ req.orgnizationName || '—' }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span :class="statusClass(req.httpStatus)">{{ req.httpStatus || '—' }}</span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <button @click="openModal(req)"
                    class="inline-flex items-center px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50
                    border border-green-200 rounded-md hover:bg-green-100 transition-colors">
                    <i class="fa-solid fa-code mr-1.5"></i>
                    View Payload
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Request / Response Modal -->
      <Modal :show="showModal" title="Request & Response Payload" size="max-w-3xl" @close="showModal = false">
        <div v-if="selectedRequest" class="space-y-5">
          <div class="flex flex-wrap gap-3 text-xs">
            <span class="inline-flex items-center px-2.5 py-1 rounded-md bg-gray-100 text-gray-600 font-mono">
              <i class="fa-solid fa-clock mr-1.5 text-gray-400"></i>
              {{ formatDate(selectedRequest.createdAt) }} {{ formatTime(selectedRequest.createdAt) }}
            </span>
            <span class="inline-flex items-center px-2.5 py-1 rounded-md bg-gray-100 text-gray-600 font-mono">
              <i class="fa-solid fa-link mr-1.5 text-gray-400"></i>
              {{ selectedRequest.endpoint || 'No endpoint' }}
            </span>
            <span :class="['inline-flex items-center px-2.5 py-1 rounded-md font-semibold', statusClass(selectedRequest.httpStatus)]">
              {{ selectedRequest.httpStatus || '—' }}
            </span>
          </div>
          <div>
            <h4 class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center">
              <i class="fa-solid fa-arrow-up-from-bracket mr-2 text-blue-400"></i>Request
            </h4>
            <pre class="bg-gray-900 text-green-300 text-xs rounded-md p-4 overflow-x-auto whitespace-pre-wrap break-words max-h-52">{{ prettyJson(selectedRequest.request) }}</pre>
          </div>
          <div>
            <h4 class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center">
              <i class="fa-solid fa-arrow-down-to-bracket mr-2 text-emerald-400"></i>Response
            </h4>
            <pre class="bg-gray-900 text-green-300 text-xs rounded-md p-4 overflow-x-auto whitespace-pre-wrap break-words max-h-52">{{ prettyJson(selectedRequest.response) }}</pre>
          </div>
        </div>
      </Modal>
    </div>
  </main>
</template>
<script setup lang="ts">
import { onMounted, ref } from 'vue';
import Header from '@/components/header.vue';
import SideBar from '@/components/sidebar.vue';
import Modal from '@/components/modal.vue';
import { useAPIKeysStore } from '@/store/apikeys';
import { useRequestStore } from '@/store/requests';
import { useUserStore } from '@/store/users';
import { useAuthStore } from '@/store/auth';
import type { APIRequest } from '@/config/types/responses';

const apiKeysStore = useAPIKeysStore();
const requestStore = useRequestStore();
const userStore = useUserStore();
const authStore = useAuthStore();

const showModal = ref(false);
const selectedRequest = ref<APIRequest | null>(null);

function openModal(req: APIRequest) {
  selectedRequest.value = req;
  showModal.value = true;
}

function formatDate(input: string) {
  if (!input) return '—';
  const date = new Date(input);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: '2-digit', year: 'numeric' }).format(date);
}

function formatTime(input: string) {
  if (!input) return '';
  const date = new Date(input);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }).format(date);
}

function prettyJson(raw: string) {
  if (!raw) return '—';
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

function statusClass(status: string) {
  const code = parseInt(status, 10);
  if (code >= 200 && code < 300) return 'px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-700';
  if (code >= 400 && code < 500) return 'px-2 py-0.5 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-700';
  if (code >= 500) return 'px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-700';
  return 'px-2 py-0.5 text-xs font-semibold rounded-full bg-gray-100 text-gray-600';
}

onMounted(async () => {
  const purpose = localStorage.getItem('purpose');
  if (purpose === 'public') {
    await apiKeysStore.getAPIKeyStats();
    await apiKeysStore.getAPIRequests(1);
    await userStore.fetchUsers(1);
    await requestStore.fetchPhoneValidations(1);
    await requestStore.fetchIDValidations(1);
  } else if (purpose === 'local') {
    await apiKeysStore.getAdminAPIKeyStats();
    await apiKeysStore.getAdminAPIRequests(1);
    await userStore.fetchAdminUsers(1);
    await requestStore.fetchAdminPhoneValidations(1);
    await requestStore.fetchAdminIDValidations(1);
  }
});
</script>
